if (!customElements.get('media-gallery')) {
    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        constructor() {
          super();
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
          this.mql = window.matchMedia('(min-width: 750px)');
          
          // Load all variants data for SKU lookup
          this.loadVariantsData();
          
          // Listen for variant picker changes
          this.setupVariantChangeListeners();
          
          // Apply initial filtering after a brief delay to ensure DOM is ready
          requestAnimationFrame(() => {
            setTimeout(() => this.filterByCurrentVariant(), 100);
          });
          
          if (!this.elements.thumbnails) return;
  
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
  
        }
        
        loadVariantsData() {
          const variantsScript = document.querySelector(
            'variant-selects script[type="application/json"][data-all-variants]'
          );
          if (variantsScript) {
            try {
              this.allVariants = JSON.parse(variantsScript.textContent || '[]');
            } catch (e) {
              console.warn('[SKU GALLERY] Failed to parse all variants JSON', e);
              this.allVariants = [];
            }
          } else {
            this.allVariants = [];
          }
        }
        
        setupVariantChangeListeners() {
          // Use event delegation on document to catch variant changes
          // This works even if variant-selects is rendered after media-gallery
          
          // Listen for change events (works for selects and standard radio buttons)
          document.addEventListener('change', (event) => {
            const target = event.target;
            const variantSelects = target.closest('variant-selects');
            if (!variantSelects) return;
            
            const isRadio = target.matches('input[type="radio"]');
            const isSelect = target.matches('select');
            
            if (isRadio || isSelect) {
              this.onVariantInputChange();
            }
          });
          
          // Also listen for clicks on swatch labels/inputs (they may not fire change consistently)
          document.addEventListener('click', (event) => {
            const target = event.target;
            
            // Check if clicking on a swatch or its label
            const swatchInput = target.closest('.swatch-input') || 
                               target.closest('[class*="swatch"]') ||
                               target.closest('label[for]');
            
            if (!swatchInput) return;
            
            const variantSelects = target.closest('variant-selects');
            if (!variantSelects) return;
            
            // Delay to let the radio button state update
            this.onVariantInputChange();
          });
        }
        
        onVariantInputChange() {
          // Small delay to let form state settle
          setTimeout(() => this.filterByCurrentVariant(), 50);
        }
        
        filterByCurrentVariant() {
          const selectedVariant = this.getSelectedVariant();
          if (selectedVariant && selectedVariant.sku) {
            this.filterGalleryBySku(selectedVariant.sku);
          } else {
            this.showAllMedia();
          }
        }
        
        getSelectedVariant() {
          const variantSelects = document.querySelector('variant-selects');
          
          // Try to load variants data if not loaded yet
          if (!this.allVariants || this.allVariants.length === 0) {
            this.loadVariantsData();
          }
          
          if (!variantSelects || this.allVariants.length === 0) {
            // Fallback to the static selected variant JSON
            const selectedVariantScript = document.querySelector(
              'variant-selects script[type="application/json"][data-selected-variant]'
            );
            if (selectedVariantScript) {
              try {
                return JSON.parse(selectedVariantScript.textContent || '{}');
              } catch (e) {
                return null;
              }
            }
            return null;
          }
          
          // Get selected option values from form inputs
          const selectedOptions = [];
          
          // Check radio buttons (swatches and buttons)
          variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            if (checked) {
              selectedOptions.push(checked.value);
            }
          });
          
          // Check dropdowns
          variantSelects.querySelectorAll('select').forEach((select) => {
            if (select.value) {
              selectedOptions.push(select.value);
            }
          });
          
          // Find matching variant
          const matchingVariant = this.allVariants.find((variant) => {
            const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
            return selectedOptions.every((opt, idx) => variantOptions[idx] === opt);
          });
          
          return matchingVariant || null;
        }
        
        filterGalleryBySku(sku) {
          if (!sku) {
            this.showAllMedia();
            return;
          }
          
          const skuUpper = sku.trim().toUpperCase();
          const viewerSlides = this.elements.viewer
            ? this.elements.viewer.querySelectorAll('.product__media-item')
            : [];
          const thumbnails = this.elements.thumbnails
            ? this.elements.thumbnails.querySelectorAll('.thumbnail-list__item')
            : [];
          
          let matched = 0;
          let firstMatchedSlide = null;
          
          const toggle = (nodes, isViewer = false) => {
            nodes.forEach((el) => {
              const imgEl = el.querySelector('img');
              const altText = imgEl && imgEl.alt ? imgEl.alt.trim() : '';
              
              const hasAnySkuPattern = altText.toUpperCase().includes('UNT');
              const hasThisSku = altText.toUpperCase().includes(skuUpper);
              
              // Images with no "UNT" pattern are generic and always shown
              if (!hasAnySkuPattern || hasThisSku) {
                el.style.display = '';
                if (hasThisSku) {
                  matched += 1;
                  if (isViewer && !firstMatchedSlide) {
                    firstMatchedSlide = el;
                  }
                }
              } else {
                el.style.display = 'none';
              }
            });
          };
          
          toggle(viewerSlides, true);
          toggle(thumbnails, false);
          
          // If no matches found, show all media
          if (matched === 0) {
            this.showAllMedia();
            return;
          }
          
          // Navigate to the first matching slide
          if (firstMatchedSlide && firstMatchedSlide.dataset.mediaId) {
            this.setActiveMedia(firstMatchedSlide.dataset.mediaId, false);
          }
        }
  
        onSlideChanged(event) {
          const current = event.detail.currentElement;

          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${current.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
  
  
        setActiveMedia(mediaId, prepend) {
          const activeMedia =
            this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (!activeMedia) {
            return;
          }
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia?.classList?.add('is-active');
  
          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
  
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
  
            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }
  
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
          this.playActiveMedia(activeMedia);

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
  
        setActiveThumbnail(thumbnail) {
          if (!this.elements.thumbnails || !thumbnail) return;
  
          this.elements.thumbnails
            .querySelectorAll('button')
            .forEach((element) => element.removeAttribute('aria-current'));
          thumbnail.querySelector('button').setAttribute('aria-current', true);
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
  
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
  
        announceLiveRegion(activeItem, position) {
          const image = activeItem.querySelector('.product__modal-opener--image img');
          if (!image) return;
          image.onload = () => {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => {
              this.elements.liveRegion.setAttribute('aria-hidden', true);
            }, 2000);
          };
          image.src = image.src;
        }
  
        playActiveMedia(activeItem) {
          window.pauseAllMedia();
          const deferredMedia = activeItem.querySelector('.deferred-media');
          if (deferredMedia) deferredMedia.loadContent(false);
        }
  
        preventStickyHeader() {
          this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
          if (!this.stickyHeader) return;
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }
  
        removeListSemantic() {
          if (!this.elements.viewer.slider) return;
          this.elements.viewer.slider.setAttribute('role', 'presentation');
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
  
        showAllMedia() {
          const viewerSlides = this.elements.viewer
            ? this.elements.viewer.querySelectorAll('.product__media-item')
            : [];
          const thumbnails = this.elements.thumbnails
            ? this.elements.thumbnails.querySelectorAll('.thumbnail-list__item')
            : [];
  
          viewerSlides.forEach((el) => {
            el.style.display = '';
          });
          thumbnails.forEach((el) => {
            el.style.display = '';
          });
        }
  
      }
    );
    // Extra safety - filter when variant changes via custom event
  document.addEventListener('variant:change', () => {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !gallery.filterByCurrentVariant) return;
    gallery.filterByCurrentVariant();
  });
  
  }
  